// @spec BCP-STR-052
'use client';

import { createFileRoute, useNavigate } from '@tanstack/react-router';
import { Shield } from 'lucide-react';

import { useTranslation } from 'react-i18next';

import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { AuditorBCPExercisesList } from '@/features/auditor-access/components/AuditorBCPExercisesList';
import { AuditorModeBanner } from '@/features/auditor-access/components/AuditorModeBanner';
import { useAuditorStore } from '@/stores/auditor-store';

export const Route = createFileRoute('/auditor/access/$accessId/bcp/exercises')({
  component: AuditorBCPExercisesPage,
});

/**
 * Auditor BCP exercises page.
 *
 * Displays a read-only list of business continuity exercises.
 */
function AuditorBCPExercisesPage() {
  const { accessId } = Route.useParams();
  const navigate = useNavigate();

  const { t } = useTranslation('auditor-access');

  const isAuditorMode = useAuditorStore.use.isAuditorMode();
  const currentAccess = useAuditorStore.use.currentAccess();

  // Verify user is in auditor mode and has the correct access ID
  if (!isAuditorMode || !currentAccess) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-4xl">
          <Alert variant="destructive">
            <Shield className="h-4 w-4" />
            <AlertTitle>{t('routes.accessRequired')}</AlertTitle>
            <AlertDescription>
              {t('routes.accessRequiredDescription')}
            </AlertDescription>
          </Alert>
          <Button variant="outline" className="mt-4" onClick={() => void navigate({ to: '/' })}>
            Go to Home
          </Button>
        </div>
      </div>
    );
  }

  // Verify the access ID matches the current auditor mode
  if (currentAccess.accessId !== accessId) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-4xl">
          <Alert variant="destructive">
            <Shield className="h-4 w-4" />
            <AlertTitle>{t('routes.invalidAccess')}</AlertTitle>
            <AlertDescription>
              {t('routes.invalidAccessDescription')}
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  // Verify the access scope includes 'bcp_plans'
  if (!currentAccess.accessScope.includes('bcp_plans')) {
    return (
      <div className="min-h-screen bg-background p-8">
        <div className="mx-auto max-w-4xl">
          <Alert variant="destructive">
            <Shield className="h-4 w-4" />
            <AlertTitle>{t('routes.accessDenied')}</AlertTitle>
            <AlertDescription>
              Your auditor access does not include permission to view BCP data.
            </AlertDescription>
          </Alert>
        </div>
      </div>
    );
  }

  const handleBackToOverview = () => {
    void navigate({ to: `/auditor/access/${accessId}/bcp` });
  };

  return (
    <div className="min-h-screen bg-background">
      <AuditorModeBanner hasOtherOrganizations={false} />

      <div className="p-8">
        <div className="mx-auto max-w-6xl">
          <AuditorBCPExercisesList accessId={accessId} onBackToOverview={handleBackToOverview} />
        </div>
      </div>
    </div>
  );
}
